/**
 * FrLocale — wraps any English SEO page to serve a French-Canadian
 * mirror at a distinct `/fr/*` URL.
 *
 * What it does on mount:
 *   1. Sets `<html lang="fr-CA">` so Google + screen readers know the
 *      page is French. Restores the previous value on unmount.
 *   2. Adds two `<link rel="alternate">` tags — one hreflang="en-CA"
 *      pointing to the English mirror, one hreflang="fr-CA" pointing
 *      to the current URL — so Google clusters both pages under one
 *      hreflang group and never treats them as duplicate content.
 *   3. Adds a `<link rel="canonical">` pointing to the French URL so
 *      the FR page ranks in its own right for QC / fr-CA queries.
 *   4. Triggers the LanguageSwitcher's existing "pick FR" flow — the
 *      whole page renders in Québécois French through Google Translate
 *      + the in-house i18n dictionary (see /lib/i18n.jsx).
 *
 * Usage in App.js:
 *   <Route path="/fr/pricing" element={<FrLocale enPath="/pricing"><Pricing/></FrLocale>} />
 */
import { useEffect } from "react";
import { useLocation } from "react-router-dom";

const SITE = "https://centralrentals.ca";

export default function FrLocale({ children, enPath }) {
  const loc = useLocation();
  useEffect(() => {
    const html = document.documentElement;
    const prevLang = html.getAttribute("lang");
    html.setAttribute("lang", "fr-CA");
    // Ensure Google Translate switches to French silently. We only
    // set the cookie — the LanguageSwitcher's mutation observer picks
    // it up and drives the actual UI translation.
    try {
      document.cookie = `googtrans=/en/fr; path=/; SameSite=Lax`;
      document.cookie = `googtrans=/en/fr; domain=.${window.location.hostname}; path=/; SameSite=Lax`;
    } catch (_) { /* ignore */ }

    // Inject hreflang + canonical link tags. We tag them with a
    // data-fr-locale attribute so we can safely clean up on unmount.
    const frUrl = `${SITE}${loc.pathname}${loc.search || ""}`;
    const enUrl = `${SITE}${enPath || loc.pathname.replace(/^\/fr\//, "/")}`;

    const links = [];
    const add = (rel, hreflang, href) => {
      const el = document.createElement("link");
      el.setAttribute("rel", rel);
      if (hreflang) el.setAttribute("hreflang", hreflang);
      el.setAttribute("href", href);
      el.setAttribute("data-fr-locale", "1");
      document.head.appendChild(el);
      links.push(el);
    };
    add("canonical", null, frUrl);
    add("alternate", "fr-CA", frUrl);
    add("alternate", "en-CA", enUrl);
    add("alternate", "x-default", enUrl);

    // Update the page description tag if present (Google surfaces this
    // in fr-CA search snippets).
    let desc = document.querySelector('meta[name="description"]');
    const prevDesc = desc?.getAttribute("content");
    if (desc) {
      desc.setAttribute("content",
        "Central Rentals — logiciel canadien de gestion immobilière. Percevez les loyers, sélectionnez les locataires, générez les baux Loi 41 et exportez vos rapports fiscaux T776.");
    }

    return () => {
      if (prevLang) html.setAttribute("lang", prevLang);
      else html.removeAttribute("lang");
      links.forEach((l) => l.remove());
      if (desc && prevDesc) desc.setAttribute("content", prevDesc);
    };
  }, [loc.pathname, loc.search, enPath]);

  return children;
}
